/**
 * @file Feature.tsx
 * @module Feature
 * @description Kopīgās Feature utilītfunkcijas — WKT ģeometriju nolasīšana,
 *              meklēšanas rezultātu objektu veidošana un Nominatim/VZD/LVC
 *              meklēšanas rezultātu tipu definēšana. Izmanto Karšu pārlūka
 *              meklēšanas josla un kartes klikšķa/atlases loģika.
 */

import GeoJSON from "ol/format/GeoJSON.js";
import { Feature } from "ol";
import { useObservable } from "@ngneat/use-observable";
import { getMapStore } from "./Map.repository";
import VectorSource from "ol/source/Vector";
import { useCallback, useEffect } from "react";
import { Circle, Fill, Stroke, Style } from "ol/style";
import WKT from "ol/format/WKT.js";

/** Search result from the Nominatim geocoding service (OSM-based) */
export interface NominatimObject {
	osm_type: string;
	osm_id: any;
	display_name: string;
	type_displayname: string;
	feature: Feature;
	source: "OSM";
}

/** Generic search result from any source (Nominatim, backend, etc.) */
export interface SearchResultObject {
	display_name: string;
	source: string;
	feature: Feature;
}

/** GeoJSON geometry shape (Point, LineString, Polygon, etc.) */
export interface Geojson {
	type: string;
	coordinates: number[];
}

function getStyle() {
	return new Style({
		stroke: new Stroke({
			width: 4,
			color: "orange",
		}),
		image: new Circle({
			fill: new Fill({
				color: "rgb(255 195 45 / 0.5)",
			}),
			stroke: new Stroke({
				width: 3,
				color: "orange",
			}),
			radius: 5,
		}),
	});
}

/**
 * Reads a GeoJSON geometry string and reprojects from EPSG:4326 (WGS84) to EPSG:3857.
 * Used for Nominatim/OSM search results which return WGS84 coordinates.
 */
export function readGJSONFeature(geometry: string) {
	const reader = new GeoJSON();
	return reader.readFeature(geometry, {
		dataProjection: "EPSG:4326",
		featureProjection: "EPSG:3857",
	});
}

/**
 * Reads a WKT geometry string and reprojects to EPSG:3857 (Web Mercator).
 * Default source projection is EPSG:3059 (LKS-92, Latvian national coordinate system),
 * which is used by the backend PostGIS database for all geodata storage.
 *
 * @param geometry - WKT geometry string
 * @param projection - Source projection (defaults to EPSG:3059)
 */
export function readWKTFeature(geometry: string, projection?: string) {
	const reader = new WKT();
	return reader.readFeature(geometry, {
		dataProjection: projection ?? "EPSG:3059",
		featureProjection: "EPSG:3857",
	});
}

/**
 * Component that adds a single search result feature to the map's "search" layer
 * with an orange circle/stroke highlight style. Renders nothing visually.
 */
function SimpleFeature(object: any) {
	const [map] = useObservable(getMapStore());

	const loadDataOnlyOnce = useCallback(() => {
		const feature = readGJSONFeature(object.feature);
		//@ts-expect-error
		feature.setId(object.feature.osm_id);
		map.map?.getAllLayers().forEach((layer) => {
			if (layer.getProperties().id === "search") {
				//@ts-expect-error
				feature.setStyle(getStyle());
				(layer.getSource() as VectorSource<any>).addFeature(feature);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [object]);

	useEffect(() => {
		if (!map?.map) return;
		loadDataOnlyOnce();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map?.map]);

	return <></>;
}

export default SimpleFeature;
